import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { navItems } from '../data/mockData';
import { ChevronDown, Menu, X, LayoutDashboard, Activity, HeartPulse, LogIn, User, LogOut } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [pagesDropdownOpen, setPagesDropdownOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const handleLogHealthData = () => {
    window.dispatchEvent(new CustomEvent('openHealthForm'));
  };

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo - links to main website */}
          <a href="https://healthchoices.health" className="flex items-center gap-2" target="_blank" rel="noopener noreferrer">
            <img
              src="https://healthchoices.health/wp-content/uploads/2025/10/logo-1-296x86.png"
              alt="Health Choices"
              className="h-12 w-auto"
              data-testid="header-logo"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-5">
            {navItems.map((item) => (
              <Link key={item.name} to={item.path}
                className={`text-sm font-medium transition-colors hover:text-[#166534] ${
                  location.pathname === item.path ? 'text-[#166534]' : 'text-gray-700'
                }`}>
                {item.name}
              </Link>
            ))}
            <Link to="/dashboard"
              className={`text-sm font-medium transition-colors hover:text-[#166534] flex items-center gap-1 ${
                location.pathname === '/dashboard' ? 'text-[#166534]' : 'text-gray-700'
              }`}>
              <LayoutDashboard className="w-4 h-4" /> Dashboard
            </Link>
            <Link to="/health"
              className={`text-sm font-medium transition-colors hover:text-[#166534] flex items-center gap-1 ${
                location.pathname === '/health' ? 'text-[#166534]' : 'text-gray-700'
              }`}>
              <Activity className="w-4 h-4" /> Health Tracking
            </Link>
            <div className="relative">
              <button onClick={() => setPagesDropdownOpen(!pagesDropdownOpen)}
                className="flex items-center gap-1 text-sm font-medium text-gray-700 hover:text-[#166534] transition-colors">
                Pages <ChevronDown className={`w-4 h-4 transition-transform ${pagesDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {pagesDropdownOpen && (
                <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 py-2 z-50">
                  <Link to="/about" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#166534]"
                    onClick={() => setPagesDropdownOpen(false)}>About Us</Link>
                  <Link to="/contact" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#166534]"
                    onClick={() => setPagesDropdownOpen(false)}>Contact</Link>
                  <Link to="/faq" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#166534]"
                    onClick={() => setPagesDropdownOpen(false)}>FAQ</Link>
                </div>
              )}
            </div>
          </nav>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <Button
              onClick={handleLogHealthData}
              className="bg-gradient-to-r from-[#22c55e] to-[#166534] hover:from-[#16a34a] hover:to-[#14532d] text-white px-5 py-2.5 rounded-lg font-medium transition-all duration-300 hover:shadow-lg hover:shadow-green-200 gap-2"
              data-testid="header-log-health-btn"
            >
              <HeartPulse className="w-4 h-4" />
              Log Health Data
            </Button>
            {user ? (
              <div className="relative">
                <button onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                  data-testid="user-menu-btn">
                  <div className="w-8 h-8 bg-[#166534] rounded-full flex items-center justify-center">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-sm font-medium text-gray-700 max-w-[100px] truncate">{user.name || user.email}</span>
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 py-2 z-50">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">{user.name}</p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                    </div>
                    <button onClick={() => { setUserMenuOpen(false); logout(); navigate('/'); }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                      data-testid="logout-btn">
                      <LogOut className="w-4 h-4" /> Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login">
                <Button variant="outline" className="border-[#166534] text-[#166534] hover:bg-[#166534] hover:text-white px-5 py-2.5 rounded-lg font-medium transition-all duration-300 gap-2"
                  data-testid="header-login-btn">
                  <LogIn className="w-4 h-4" /> Sign In
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-100">
            <nav className="flex flex-col gap-4">
              {navItems.map((item) => (
                <Link key={item.name} to={item.path}
                  className={`text-base font-medium ${location.pathname === item.path ? 'text-[#166534]' : 'text-gray-700'}`}
                  onClick={() => setMobileMenuOpen(false)}>
                  {item.name}
                </Link>
              ))}
              <Link to="/dashboard"
                className={`text-base font-medium flex items-center gap-1 ${location.pathname === '/dashboard' ? 'text-[#166534]' : 'text-gray-700'}`}
                onClick={() => setMobileMenuOpen(false)}>
                <LayoutDashboard className="w-4 h-4" /> Dashboard
              </Link>
              <Link to="/health"
                className={`text-base font-medium flex items-center gap-1 ${location.pathname === '/health' ? 'text-[#166534]' : 'text-gray-700'}`}
                onClick={() => setMobileMenuOpen(false)}>
                <Activity className="w-4 h-4" /> Health Tracking
              </Link>
              <button onClick={() => { setMobileMenuOpen(false); handleLogHealthData(); }}
                className="mt-2 w-full">
                <Button className="w-full bg-gradient-to-r from-[#22c55e] to-[#166534] text-white gap-2" data-testid="mobile-log-health-btn">
                  <HeartPulse className="w-4 h-4" /> Log Health Data
                </Button>
              </button>
              {user ? (
                <button onClick={() => { setMobileMenuOpen(false); logout(); navigate('/'); }}
                  className="mt-1 w-full">
                  <Button variant="outline" className="w-full border-red-300 text-red-600 gap-2">
                    <LogOut className="w-4 h-4" /> Sign Out
                  </Button>
                </button>
              ) : (
                <Link to="/login" className="mt-1" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="outline" className="w-full border-[#166534] text-[#166534] gap-2">
                    <LogIn className="w-4 h-4" /> Sign In
                  </Button>
                </Link>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
